import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaPlus, FaSyncAlt, FaTimes } from "react-icons/fa";
import { deleteUser, getUsers, registerUser, updateUserDetails } from "../../utils/user";
import { ClipLoader, RingLoader } from "react-spinners";
import { toast } from "sonner";

const UsersPage = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userRole, setUserRole] = useState("super_admin");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);

    // Fetch users
    useEffect(() => {
        const loadUsers = async () => {
            try {
                const data = await getUsers();
                setUsers(data.users);
            } catch {
                setError("Failed to fetch users. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        loadUsers();
    }, []);

    const itemsPerPage = 8;
    const totalPages = Math.ceil(users.length / itemsPerPage);

    const handleEdit = (user) => {
        if (userRole === "super_admin") {
            setSelectedUser(user);
            setIsEditModalOpen(true);
        } else {
            toast.error("Only super admins can edit users");
        }
    };

    const handleDelete = (id) => {
        if (userRole !== "super_admin") {
            toast.error("Only super admins can delete users");
            return;
        }

        // Non-blocking confirmation via a sonner action toast (was a native prompt).
        toast("Delete this user?", {
            description: "This can't be undone.",
            action: {
                label: "Delete",
                onClick: async () => {
                    try {
                        setLoading(true);
                        await deleteUser(id);
                        setUsers((prev) => prev.filter((user) => user.user_id !== id));
                        toast.success("User deleted");
                    } catch {
                        toast.error("Failed to delete user", { description: "Please try again later." });
                    } finally {
                        setLoading(false);
                    }
                },
            },
            cancel: { label: "Cancel" },
        });
    };

    const handleUpdateUser = async () => {
        await updateUserDetails(selectedUser.user_id, selectedUser);
        setUsers(users.map(user => user.user_id === selectedUser.user_id ? selectedUser : user));
        setIsEditModalOpen(false);
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "next" && prev < totalPages) return prev + 1;
            if (direction === "prev" && prev > 1) return prev - 1;
            return prev;
        });
    };

    const currentUsers = users.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const PaginationButton = ({ direction, disabled, onClick }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center justify-center h-9 w-9 rounded-xl text-sm font-semibold transition-all ${disabled
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#2148C0] text-white hover:bg-blue-700 shadow-sm"
                }`}
            aria-label={`Go to ${direction} page`}
        >
            {direction === "prev" ? "‹" : "›"}
        </button>
    );

    const [newUser, setNewUser] = useState({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
    });

    const handleAddUser = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const savedUser = await registerUser(newUser);
            setUsers([...users, newUser]);
            setNewUser({ firstName: "", lastName: "", phoneNumber: "", email: "" });
            setIsModalOpen(false);
        } catch {
            setSaveError("Failed to save user. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = async () => {
        setLoading(true);
        try {
            const data = await getUsers();
            setUsers(data.users);
        } catch {
            setError("Failed to refresh users. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <RingLoader color="#4A90E2" size={50} />
                <p className="ml-4 text-blue-600 font-medium">Loading your users...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Hero */}
            <div className="mb-8 bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg shadow-[#2148C0]/20">
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-white/10 rounded-full" />
                <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white text-xs font-medium mb-3">
                            User Management
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                            Users
                        </h1>
                        <p className="text-blue-100 mt-1">
                            {users.length.toLocaleString()} total accounts on Teechaa.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {userRole === "super_admin" && (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#2148C0] hover:bg-blue-50 text-sm font-semibold shadow"
                            >
                                <FaPlus /> Add User
                            </button>
                        )}
                        <button
                            onClick={handleRefresh}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/30 text-white hover:bg-white/20 text-sm font-semibold"
                        >
                            <FaSyncAlt /> Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-blue-50/60 text-left">
                            <tr className="text-xs uppercase tracking-[0.14em] text-[#2148C0]">
                                <th className="px-5 py-3 font-semibold">First Name</th>
                                <th className="px-5 py-3 font-semibold">Last Name</th>
                                <th className="px-5 py-3 font-semibold">Phone</th>
                                <th className="px-5 py-3 font-semibold">Email</th>
                                <th className="px-5 py-3 font-semibold text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentUsers.map((user) => (
                                <tr
                                    key={user.user_id}
                                    className="border-t border-gray-100 hover:bg-blue-50/40"
                                >
                                    <td className="px-5 py-3 font-semibold text-gray-900">{user.firstName}</td>
                                    <td className="px-5 py-3 text-gray-700">{user.lastName}</td>
                                    <td className="px-5 py-3 text-gray-700">{user.phoneNumber || user.phone}</td>
                                    <td className="px-5 py-3 text-gray-700">{user.email}</td>
                                    <td className="px-5 py-3 text-center">
                                        {userRole === "super_admin" && (
                                            <div className="inline-flex items-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="p-2 rounded-lg bg-blue-50 text-[#2148C0] hover:bg-blue-100"
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.user_id)}
                                                    className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center px-5 py-4 border-t border-gray-100 bg-gray-50/40">
                    <PaginationButton
                        direction="prev"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange("prev")}
                    />
                    <span className="text-gray-700 text-sm font-medium">
                        Page {currentPage} of {totalPages || 1}
                    </span>
                    <PaginationButton
                        direction="next"
                        disabled={currentPage === totalPages || totalPages === 0}
                        onClick={() => handlePageChange("next")}
                    />
                </div>
            </div>
        </div>
    );
};

export default UsersPage;
